// Shared accordion-section shell for the left library rail.
//
// Visual contract per DESIGN.md "Phosphor Rack": phosphor LED dot,
// uppercase Space Mono eyebrow, count tucked at the right edge,
// optional inline action button, single yellow hairline at the
// header's bottom edge that doubles as the section divider when
// collapsed. The header is always visible. The body renders only
// while expanded, and grows to fill the rail's vertical run.

import type { ReactNode } from 'react';

export type SectionCardProps = {
  /** Stable id used for aria-controls + the toggle button id. */
  id: string;
  /** Eyebrow label (rendered uppercase via tracking; pass plain). */
  label: string;
  /** Live count, "···" while loading, or undefined to hide. */
  count?: ReactNode;
  /** One-line tagline directly under the label when expanded. */
  description?: ReactNode;
  /** Tailwind background class for the LED dot. Defaults to the
   *  primary phosphor (`bg-yellow-300`). Use voice-LED tokens
   *  (`bg-emerald-300` etc.) to mark sections by ordinal identity. */
  accentDot?: string;
  /** Tailwind text-color class for the label + chevron. Pairs with
   *  accentDot when a section uses a non-primary accent. */
  accentInk?: string;
  expanded: boolean;
  onToggle: () => void;
  /** Optional inline action — rendered as a small button to the
   *  right of the count. Stops propagation so it doesn't toggle. */
  action?: {
    label: ReactNode;
    title?: string;
    onClick: () => void;
  };
  /** When true, the LED pulses (animate-pulse). Used by Learn to flash
   *  the Songs section when a snippet is "ejected" into the library. */
  pulse?: boolean;
  children?: ReactNode;
};

export function SectionCard({
  id,
  label,
  count,
  description,
  accentDot = 'bg-yellow-300',
  accentInk = 'text-yellow-300',
  expanded,
  onToggle,
  action,
  pulse = false,
  children,
}: SectionCardProps) {
  const headingId = `section-${id}-heading`;
  const bodyId = `section-${id}-body`;

  return (
    <section
      aria-labelledby={headingId}
      className={
        'flex flex-col min-h-0 ' +
        (expanded ? 'flex-1' : 'shrink-0')
      }
    >
      <header className="relative bg-gradient-to-b from-zinc-900/50 to-transparent">
        <div className="flex items-center gap-2 px-4 py-3">
          <span
            aria-hidden="true"
            className={
              'h-1.5 w-1.5 rounded-full shadow-[0_0_8px_rgba(253,224,71,0.6)] ' +
              accentDot +
              (pulse ? ' animate-pulse' : '')
            }
          />
          <button
            id={headingId}
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={bodyId}
            className={
              'inline-flex items-center gap-1.5 font-display leading-none ' +
              'text-[10px] tracking-[0.38em] uppercase hover:brightness-110 ' +
              accentInk
            }
          >
            {label}
            <span
              aria-hidden="true"
              className={
                'text-[8px] transition-transform duration-150 ' + accentInk + '/70 ' +
                (expanded ? 'rotate-180' : '')
              }
            >
              ▾
            </span>
          </button>

          {count !== undefined && (
            <span className="text-[9px] text-zinc-600 tabular-nums tracking-wider ml-1">
              {count}
            </span>
          )}

          <div className="ml-auto flex items-center gap-1">
            {action && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  action.onClick();
                }}
                title={action.title}
                className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-yellow-300 px-1.5 py-0.5 rounded transition-colors"
              >
                {action.label}
              </button>
            )}
          </div>
        </div>

        {expanded && description !== undefined && (
          <div className="px-4 pb-3 -mt-1 text-[10px] text-zinc-500 leading-[1.5] tracking-[0.04em]">
            {description}
          </div>
        )}

        <div
          aria-hidden="true"
          className="absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-transparent via-yellow-300/30 to-transparent"
        />
      </header>

      {expanded && (
        <div
          id={bodyId}
          role="region"
          aria-labelledby={headingId}
          className="flex-1 min-h-0 flex flex-col"
        >
          {children}
        </div>
      )}
    </section>
  );
}
